import React, { FC, memo, useCallback, useState } from 'react';

import Button from '@smart-tv/components/button/button';
import DeleteIcon from '@smart-tv/components/icons/delete';
import EditIcon from '@smart-tv/components/icons/edit';
import Typography from '@smart-tv/components/typography';

import testIds from '@smart-tv/e2e/shared/variables';
import { NetworkErrorDialog } from '@smart-tv/components/error-dialog/network-error-dialog';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import useFavoritesEdit from '@smart-tv/hooks/use-favorites-edit';
import { FavoritesScreen } from '@smart-tv/contexts/favorites-edit-context/favorites-edit-context';
import styles from '../favorites.module.scss';

enum FavoritesHomeListButtonsValue {
  Edit = 'EDIT',
  Delete = 'DELETE'
}

export enum FailedActionSource {
  EditFavoritesList = 'EDIT_FAVORITES_LIST',
  DeleteFavoritesList = 'DELETE_FAVORITES_LIST'
}

interface Props {
  listId: number;
}

export const FavoritesHomeListButtons: FC<Props> = ({ listId }) => {
  const { handleSelectScreen, handleSetInitialFavoriteList } = useFavoritesEdit();
  const { isOffline, setIsOffline, checkAndUpdateNetwork } = useNetwork();

  const [buttonFocused, setButtonFocused] = useState<FavoritesHomeListButtonsValue | undefined>();
  const [failedActionSource, setFailedActionSource] = useState<FailedActionSource | null>(null);

  const handleClickEditAction = useCallback(async () => {
    const hasNetwork = await checkAndUpdateNetwork();
    if (hasNetwork) {
      handleSelectScreen(FavoritesScreen.Edit);
    } else {
      setFailedActionSource(FailedActionSource.EditFavoritesList);
    }
  }, [handleSelectScreen, checkAndUpdateNetwork]);

  const handleClickDeleteAction = useCallback(async () => {
    const hasNetwork = await checkAndUpdateNetwork();
    if (hasNetwork) {
      handleSelectScreen(FavoritesScreen.Delete);
    } else {
      setFailedActionSource(FailedActionSource.DeleteFavoritesList);
    }
  }, [handleSelectScreen, checkAndUpdateNetwork]);

  const retryEditOrDeleteAction = useCallback(async () => {
    if (failedActionSource === FailedActionSource.EditFavoritesList) {
      handleClickEditAction();
    } else if (failedActionSource === FailedActionSource.DeleteFavoritesList) {
      handleClickDeleteAction();
    }
  }, [handleClickEditAction, handleClickDeleteAction, failedActionSource]);

  const closeNetworkErrorDialogAction = useCallback(() => {
    setIsOffline(false);
    setFailedActionSource(null);
  }, [setIsOffline]);

  return (
    <div className={styles['favorites-home-root--actions']}>
      {isOffline && failedActionSource && (
        <NetworkErrorDialog
          onTryAgain={retryEditOrDeleteAction}
          onClose={closeNetworkErrorDialogAction}
        />
      )}
      <div className={styles['favorites-home-root--btn-wrap']}>
        <Button
          className={styles['favorites-home-root--btn']}
          onNavFocus={() => setButtonFocused(FavoritesHomeListButtonsValue.Edit)}
          onNavBlur={() => setButtonFocused(undefined)}
          onPointerEnter={() => setButtonFocused(FavoritesHomeListButtonsValue.Edit)}
          onPointerLeave={() => setButtonFocused(undefined)}
          onClick={() => {
            handleSetInitialFavoriteList(listId);
            handleClickEditAction();
          }}
          data-testid={testIds.favorites.home.itemActions.edit}
        >
          <EditIcon size={'lg'} />
        </Button>
        {buttonFocused === FavoritesHomeListButtonsValue.Edit && (
          <Typography className={styles['favorites-home-root--btn-label']} variant="caption">
            Rediger
          </Typography>
        )}
      </div>

      <div className={styles['favorites-home-root--btn-wrap']}>
        <Button
          className={styles['favorites-home-root--btn']}
          onNavFocus={() => setButtonFocused(FavoritesHomeListButtonsValue.Delete)}
          onNavBlur={() => setButtonFocused(undefined)}
          onPointerEnter={() => setButtonFocused(FavoritesHomeListButtonsValue.Delete)}
          onPointerLeave={() => setButtonFocused(undefined)}
          onClick={() => {
            handleSetInitialFavoriteList(listId);
            handleClickDeleteAction();
          }}
          data-testid={testIds.favorites.home.itemActions.delete}
        >
          <DeleteIcon size={'lg'} />
        </Button>
        {buttonFocused === FavoritesHomeListButtonsValue.Delete && (
          <Typography className={styles['favorites-home-root--btn-label']} variant="caption">
            Slet
          </Typography>
        )}
      </div>
    </div>
  );
};

export default memo(FavoritesHomeListButtons);
